import { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { type EvidenceData } from '@/components/citations';
import { MatterClientNoteV2 } from '@/components/matter-notes-v2/matter-client-v2/MatterClientNoteV2';
import {
  OrganizationCard,
  OrganizationForm,
  PersonCard,
  PersonForm,
} from '@/components/parties';
import {
  AnimalCard,
  PropertyCard,
  VehicleCard,
  VehicleForm,
} from '@/components/subjects';
import {
  Dialog,
  DialogBody,
  DialogTitle,
} from '@/components/ui/catalyst/dialog';
import { useIncidentScene } from '@/hooks/useIncidentScene';
import { useGetBulkEntitySuggestionsQuery } from '@/services/api/entitySuggestionsService';
import {
  type CreateOrganization,
  type CreatePerson,
  type UpdateOrganization,
  type UpdatePerson,
  useOrganizationsDeleteMutation,
  useOrganizationsUpdateMutation,
  usePersonsDeleteMutation,
  usePersonsUpdateMutation,
} from '@/services/api/partiesService';
import {
  type CreateVehicle,
  type ReadVehicleWithOccupants,
  type UpdateVehicle,
  useVehiclesDeleteMutation,
  useVehiclesUpdateMutation,
} from '@/services/api/subjectsService';
import { MatterClient } from '@/services/types/matter-client-types';
import { transformSuggestionsToEvidence } from '@/utils/evidenceTransformers';

interface IncidentSceneGroupedProps {
  matterId: string;
  client?: MatterClient;
}

type EditingEntity =
  | { type: 'client' }
  | { type: 'person'; id: string }
  | { type: 'organization'; id: string }
  | { type: 'vehicle'; data: ReadVehicleWithOccupants }
  | null;

export const IncidentSceneGrouped = ({
  matterId,
  client,
}: IncidentSceneGroupedProps) => {
  const { sceneData, isLoading } = useIncidentScene(matterId);
  const [editing, setEditing] = useState<EditingEntity>(null);

  const [updatePerson] = usePersonsUpdateMutation();
  const [updateOrg] = useOrganizationsUpdateMutation();
  const [updateVehicle] = useVehiclesUpdateMutation();
  const [deletePerson] = usePersonsDeleteMutation();
  const [deleteOrg] = useOrganizationsDeleteMutation();
  const [deleteVehicle] = useVehiclesDeleteMutation();

  const allEntityIds = useMemo(() => {
    if (!sceneData) return [];
    const ids: string[] = [];
    if (sceneData.claimantPerson) ids.push(sceneData.claimantPerson.id);
    if (sceneData.claimantOrg) ids.push(sceneData.claimantOrg.id);
    sceneData.vehicleGroups.forEach((vg) => {
      ids.push(vg.vehicle.id);
      vg.occupants.forEach((p) => ids.push(p.id));
    });
    sceneData.unaffiliatedPersons.forEach((p) => ids.push(p.id));
    sceneData.unaffiliatedOrgs.forEach((o) => ids.push(o.id));
    return ids;
  }, [sceneData]);

  const { data: suggestionsData } = useGetBulkEntitySuggestionsQuery(
    {
      matterId,
      targetIds: allEntityIds,
    },
    {
      skip: allEntityIds.length === 0,
    },
  );

  const getEntityEvidence = useMemo(
    () =>
      (entityId: string): EvidenceData[] => {
        if (!suggestionsData || !suggestionsData[entityId]) return [];
        return transformSuggestionsToEvidence(suggestionsData[entityId]);
      },
    [suggestionsData],
  );

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, idx) => (
          <div
            key={idx}
            className="animate-pulse rounded-lg border border-gray-200 bg-white p-3"
          >
            <div className="h-16 rounded bg-gray-100" />
          </div>
        ))}
      </div>
    );
  }

  if (!sceneData || sceneData.isEmpty) return null;

  const hasClaimant = sceneData.claimantPerson || sceneData.claimantOrg;

  const handleSavePerson = async (data: CreatePerson | UpdatePerson) => {
    if (!('id' in data)) return;
    try {
      await updatePerson({
        matterId,
        id: data.id,
        patchedUpdatePerson: data,
      }).unwrap();
      toast.success('Person updated');
      setEditing(null);
    } catch {
      toast.error('Failed to update person');
    }
  };

  const handleSaveOrg = async (
    data: CreateOrganization | UpdateOrganization,
  ) => {
    if (!('id' in data)) return;
    try {
      await updateOrg({
        matterId,
        id: data.id,
        patchedUpdateOrganization: data,
      }).unwrap();
      toast.success('Organization updated');
      setEditing(null);
    } catch {
      toast.error('Failed to update organization');
    }
  };

  const handleSaveVehicle = async (data: CreateVehicle | UpdateVehicle) => {
    if (!('id' in data)) return;
    try {
      await updateVehicle({
        matterId,
        id: data.id,
        patchedUpdateVehicle: data,
      }).unwrap();
      toast.success('Vehicle updated');
      setEditing(null);
    } catch {
      toast.error('Failed to update vehicle');
    }
  };

  const handleDeletePerson = async (id: string) => {
    try {
      await deletePerson({ matterId, id }).unwrap();
      toast.success('Person deleted');
      setEditing(null);
    } catch {
      toast.error('Failed to delete person');
    }
  };

  const handleDeleteOrg = async (id: string) => {
    try {
      await deleteOrg({ matterId, id }).unwrap();
      toast.success('Organization deleted');
      setEditing(null);
    } catch {
      toast.error('Failed to delete organization');
    }
  };

  const handleDeleteVehicle = async (id: string) => {
    try {
      await deleteVehicle({ matterId, id }).unwrap();
      toast.success('Vehicle deleted');
      setEditing(null);
    } catch {
      toast.error('Failed to delete vehicle');
    }
  };

  const findPerson = (id: string) => {
    if (sceneData.claimantPerson?.id === id) return sceneData.claimantPerson;
    const allPersons = [
      ...sceneData.vehicleGroups.flatMap((vg) => vg.occupants),
      ...sceneData.unaffiliatedPersons,
    ];
    return allPersons.find((p) => p.id === id);
  };

  const findOrg = (id: string) => {
    if (sceneData.claimantOrg?.id === id) return sceneData.claimantOrg;
    return sceneData.unaffiliatedOrgs.find((o) => o.id === id);
  };

  const currentPerson =
    editing?.type === 'person' ? findPerson(editing.id) : null;
  const currentOrg =
    editing?.type === 'organization' ? findOrg(editing.id) : null;

  return (
    <div className="space-y-4">
      {hasClaimant && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-blue-900">
            Client
          </h3>
          {sceneData.claimantPerson && (
            <PersonCard
              person={sceneData.claimantPerson}
              showRelationships
              onEdit={() => setEditing({ type: 'client' })}
              evidence={getEntityEvidence(sceneData.claimantPerson.id)}
              evidenceTitle="Client Evidence"
              matterId={matterId}
            />
          )}
          {sceneData.claimantOrg && (
            <OrganizationCard
              organization={sceneData.claimantOrg}
              showEmployees
              onEdit={() => setEditing({ type: 'client' })}
              evidence={getEntityEvidence(sceneData.claimantOrg.id)}
              evidenceTitle="Client Evidence"
              matterId={matterId}
            />
          )}
        </div>
      )}

      {sceneData.vehicleGroups.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Vehicles
          </h3>
          {sceneData.vehicleGroups.map(({ vehicle }) => (
            <VehicleCard
              key={vehicle.id}
              vehicle={vehicle}
              showOccupants
              onEdit={() => setEditing({ type: 'vehicle', data: vehicle })}
              evidence={getEntityEvidence(vehicle.id)}
              evidenceTitle="Vehicle Evidence"
              matterId={matterId}
            />
          ))}
        </div>
      )}

      {sceneData.propertyGroups.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Properties
          </h3>
          {sceneData.propertyGroups.map(({ property }) => (
            <PropertyCard key={property.id} property={property} showOwners />
          ))}
        </div>
      )}

      {(sceneData.unaffiliatedPersons.length > 0 ||
        sceneData.unaffiliatedOrgs.length > 0 ||
        sceneData.animals.length > 0) && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-700">
            Involved Parties
          </h3>
          {sceneData.unaffiliatedPersons.map((person) => (
            <PersonCard
              key={person.id}
              person={person}
              showRelationships
              onEdit={() => setEditing({ type: 'person', id: person.id })}
              evidence={getEntityEvidence(person.id)}
              evidenceTitle="Evidence"
              matterId={matterId}
            />
          ))}

          {sceneData.unaffiliatedOrgs.map((org) => (
            <OrganizationCard
              key={org.id}
              organization={org}
              showEmployees
              onEdit={() => setEditing({ type: 'organization', id: org.id })}
              evidence={getEntityEvidence(org.id)}
              evidenceTitle="Evidence"
              matterId={matterId}
            />
          ))}

          {sceneData.animals.map((animal) => (
            <AnimalCard key={animal.id} animal={animal} />
          ))}
        </div>
      )}

      {/* Edit Client Modal */}
      {client && (
        <Dialog
          open={editing?.type === 'client'}
          onClose={() => setEditing(null)}
          size="2xl"
        >
          <DialogTitle>Edit Client</DialogTitle>
          <DialogBody>
            <MatterClientNoteV2 client={client} matterId={matterId} />
          </DialogBody>
        </Dialog>
      )}

      {/* Edit Person Modal */}
      <Dialog
        open={editing?.type === 'person'}
        onClose={() => setEditing(null)}
        size="2xl"
      >
        <DialogTitle>Edit Person</DialogTitle>
        <DialogBody>
          {currentPerson && (
            <PersonForm
              person={currentPerson}
              matterId={matterId}
              onSubmit={handleSavePerson}
              onCancel={() => setEditing(null)}
              onDelete={
                !currentPerson.is_claimant ? handleDeletePerson : undefined
              }
            />
          )}
        </DialogBody>
      </Dialog>

      {/* Edit Organization Modal */}
      <Dialog
        open={editing?.type === 'organization'}
        onClose={() => setEditing(null)}
        size="2xl"
      >
        <DialogTitle>Edit Organization</DialogTitle>
        <DialogBody>
          {currentOrg && (
            <OrganizationForm
              organization={currentOrg}
              matterId={matterId}
              onSubmit={handleSaveOrg}
              onCancel={() => setEditing(null)}
              onDelete={!currentOrg.is_claimant ? handleDeleteOrg : undefined}
            />
          )}
        </DialogBody>
      </Dialog>

      {/* Edit Vehicle Modal */}
      <Dialog
        open={editing?.type === 'vehicle'}
        onClose={() => setEditing(null)}
        size="2xl"
      >
        <DialogTitle>Edit Vehicle</DialogTitle>
        <DialogBody>
          {editing?.type === 'vehicle' && (
            <VehicleForm
              vehicle={editing.data}
              matterId={matterId}
              onSubmit={handleSaveVehicle}
              onCancel={() => setEditing(null)}
              onDelete={handleDeleteVehicle}
            />
          )}
        </DialogBody>
      </Dialog>
    </div>
  );
};
